import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import elefaAppIcon from "@/assets/elefa-app-icon.webp";

type LogoVariant = "header" | "footer" | "admin";
type LogoSize = "sm" | "md" | "lg" | "xl";

interface LogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  asLink?: boolean;
  className?: string;
  /** Render Signal-style: icon + "elefa Connect" wordmark */
  withIcon?: boolean;
  /** Show full "elefa Connect" wordmark instead of just "elefa" */
  fullName?: boolean;
}

const headerSizes: Record<LogoSize, string> = {
  sm: "text-2xl sm:text-3xl",
  md: "text-3xl sm:text-4xl md:text-5xl",
  lg: "text-4xl sm:text-5xl md:text-6xl",
  xl: "text-5xl sm:text-6xl md:text-7xl",
};

const iconSizes: Record<LogoSize, string> = {
  sm: "h-10 w-10 sm:h-12 sm:w-12",
  md: "h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20",
  lg: "h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24",
  xl: "h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28",
};

const footerSizes: Record<LogoSize, string> = {
  sm: "text-5xl",
  md: "text-6xl",
  lg: "text-7xl",
  xl: "text-8xl",
};

export function Logo({
  variant = "header",
  size = "md",
  asLink = true,
  className,
  withIcon = false,
  fullName = false,
}: LogoProps) {
  const baseClasses = "font-black tracking-tight lowercase";

  const getContent = () => {
    if (variant === "footer") {
      return (
        <span
          className={cn(
            baseClasses,
            footerSizes[size],
            "text-elefa-orange leading-none",
            className
          )}
        >
          e
        </span>
      );
    }

    const wordmark =
      variant === "admin" ? (
        <span
          className={cn(
            baseClasses,
            headerSizes[size],
            "bg-gradient-to-r from-elefa-orange to-elefa-gold bg-clip-text text-transparent leading-none"
          )}
        >
          {fullName ? "elefa Connect" : "elefa"}
        </span>
      ) : (
        <span
          className={cn(
            baseClasses,
            headerSizes[size],
            "text-elefa-orange leading-none"
          )}
        >
          {fullName ? (
            <>
              elefa<span className="text-foreground font-semibold text-[0.6em] align-middle ml-1.5 tracking-normal"> Connect</span>
            </>
          ) : (
            "elefa"
          )}
        </span>
      );

    if (withIcon) {
      return (
        <span className={cn("inline-flex items-center gap-2", className)}>
          <img
            src={elefaAppIcon}
            alt="elefa Connect"
            width={96}
            height={96}
            decoding="sync"
            loading="eager"
            // @ts-expect-error fetchpriority is a valid HTML attr
            fetchpriority="high"
            className={cn(iconSizes[size], "rounded-[22%] object-cover shrink-0")}
          />
          {wordmark}
        </span>
      );
    }

    return <span className={className}>{wordmark}</span>;
  };

  if (asLink) {
    return (
      <Link to="/" className="flex items-center">
        {getContent()}
      </Link>
    );
  }

  return getContent();
}
